import React, { useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import {
  buildHolidaysByDateMap,
  formatHolidayDate,
} from '../utils/laborCalendarUtils';
import './LaborFullCalendar.css';

export const LaborFullCalendar = ({
  events = [],
  initialDate,
  loading = false,
  onDatesChange,
  onHolidayClick,
  holidays = [],
}) => {
  const calendarKey = useMemo(
    () =>
      events
        .map((e) => `${e.id ?? ''}:${String(e.start)}:${String(e.end)}:${e.title ?? ''}`)
        .join('|'),
    [events]
  );

  const calendarMountKey = useMemo(
    () => `${initialDate || 'default'}-${calendarKey}-${holidays.length}`,
    [initialDate, calendarKey, holidays.length]
  );

  const holidaysByDate = useMemo(() => buildHolidaysByDateMap(holidays), [holidays]);

  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();
    if (!onHolidayClick) return;

    const props = info.event.extendedProps || {};
    const dateStr =
      info.event.startStr?.slice(0, 10) ||
      (props.eventDate ? formatHolidayDate(props.eventDate) : '');

    if (!dateStr) return;

    const hol = holidaysByDate.get(dateStr);
    const isRegistered =
      props.isRegisteredHoliday || Boolean(props.holidayName?.trim()) || Boolean(hol);

    if (!isRegistered) return;

    onHolidayClick({
      id: hol?.id ?? props.holidayId ?? null,
      name: hol?.name || props.holidayName || info.event.title,
      date: dateStr,
    });
  };

  return (
    <div className="labor-fc-wrapper relative min-h-[520px]">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-900/60 pointer-events-none">
          <span className="text-gray-300 text-sm">Cargando calendario…</span>
        </div>
      )}
      <FullCalendar
        key={calendarMountKey}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale={esLocale}
        initialDate={initialDate || new Date()}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek',
        }}
        height="auto"
        events={events}
        eventDisplay="block"
        dayMaxEvents={4}
        moreLinkClick="popover"
        datesSet={(arg) => {
          if (onDatesChange) {
            onDatesChange(arg.start, arg.end);
          }
        }}
        eventClassNames={(arg) => {
          const p = arg.event.extendedProps || {};
          const classes = [];
          if (p.isOnAbsence && p.absenceType) {
            classes.push('labor-fc-absence', `labor-fc-absence-${p.absenceType}`);
          }
          const dateStr = arg.event.startStr?.slice(0, 10) || '';
          const hasHoliday =
            p.isRegisteredHoliday ||
            p.holidayId ||
            (dateStr && holidaysByDate.has(dateStr));
          if (hasHoliday && onHolidayClick) {
            classes.push('labor-fc-holiday-clickable');
          }
          return classes;
        }}
        eventClick={handleEventClick}
      />
    </div>
  );
};
